/**
 * Shape Resize Helpers
 *
 * Utility functions for shape resize coordinate calculations.
 * Provides geometric calculations for shape point reconstruction.
 *
 * Key Features:
 * - Rectangle point reconstruction from opposite corners
 * - Circle cardinal point calculation with longitude correction
 * - Center and distance calculations for all shape types
 * - DRY: Eliminates duplicated resize logic between preview and final update
 *
 * @module utils/shapeResizeHelpers
 */

import type { Coordinates } from '../types';
import { getLongitudeOffset } from './geoHelpers';

/**
 * Calculate rectangle corner points from two opposite corners
 *
 * Reconstructs a valid rectangle from any two opposite corners.
 * Uses Math.min/max to ensure consistent point ordering regardless of
 * which corners are provided.
 *
 * @param corner1 - First corner (any corner)
 * @param corner2 - Opposite corner (diagonally opposite)
 * @returns Array of 4 corners: [TL, TR, BR, BL]
 *
 * @example
 * const points = calculateRectanglePoints(
 *   { lat: 52.2, lng: 21.0 },  // Top-right
 *   { lat: 52.0, lng: 20.8 }   // Bottom-left
 * );
 * // Returns: [TL(52.2, 20.8), TR(52.2, 21.0), BR(52.0, 21.0), BL(52.0, 20.8)]
 */
export function calculateRectanglePoints(
  corner1: Coordinates,
  corner2: Coordinates
): Coordinates[] {
  const minLat = Math.min(corner1.lat, corner2.lat);
  const maxLat = Math.max(corner1.lat, corner2.lat);
  const minLng = Math.min(corner1.lng, corner2.lng);
  const maxLng = Math.max(corner1.lng, corner2.lng);

  return [
    { lat: maxLat, lng: minLng }, // Top-left
    { lat: maxLat, lng: maxLng }, // Top-right
    { lat: minLat, lng: maxLng }, // Bottom-right
    { lat: minLat, lng: minLng }, // Bottom-left
  ];
}

/**
 * Calculate circle cardinal points with longitude correction
 *
 * Generates 4 cardinal direction points (N, S, E, W) at a given radius from center.
 * Applies latitude-based longitude correction to ensure East/West points are
 * positioned at the same actual distance from center as North/South points.
 *
 * @param center - Circle center coordinates
 * @param radius - Circle radius in degrees (distance from center to cardinal point)
 * @returns Array of 4 cardinal points: [North, South, East, West]
 *
 * @example
 * const points = calculateCircleCardinalPoints(
 *   { lat: 52, lng: 20 },
 *   0.0015  // ~166m
 * );
 * // Returns 4 points all at ~166m distance from center
 * // (East/West use ~0.00244° due to longitude correction at 52°N)
 */
export function calculateCircleCardinalPoints(
  center: Coordinates,
  radius: number
): Coordinates[] {
  // East/West require longitude correction due to latitude compression
  // At 52°N (Poland), 1° longitude ≈ 68km vs 1° latitude ≈ 111km
  const longitudeOffset = getLongitudeOffset(center.lat, radius);

  return [
    { lat: center.lat + radius, lng: center.lng }, // North (top)
    { lat: center.lat - radius, lng: center.lng }, // South (bottom)
    { lat: center.lat, lng: center.lng + longitudeOffset }, // East (right) - corrected
    { lat: center.lat, lng: center.lng - longitudeOffset }, // West (left) - corrected
  ];
}

/**
 * Calculate center point from array of coordinates
 *
 * Computes the geometric centroid (average position) of all points.
 *
 * @param points - Array of coordinates
 * @returns Center point (centroid)
 *
 * @example
 * const center = calculateCenter([
 *   { lat: 52.0, lng: 20.0 },
 *   { lat: 52.0, lng: 21.0 },
 *   { lat: 53.0, lng: 20.5 },
 * ]);
 * // Returns: { lat: 52.33, lng: 20.5 }
 */
export function calculateCenter(points: Coordinates[]): Coordinates {
  if (points.length === 0) {
    return { lat: 0, lng: 0 };
  }

  const sum = points.reduce(
    (acc, p) => ({
      lat: acc.lat + p.lat,
      lng: acc.lng + p.lng,
    }),
    { lat: 0, lng: 0 }
  );

  return {
    lat: sum.lat / points.length,
    lng: sum.lng / points.length,
  };
}

/**
 * Calculate distance between two coordinates in degrees
 *
 * Uses simple Euclidean distance (suitable for small distances).
 * For large distances, consider using Haversine formula.
 *
 * @param p1 - First point
 * @param p2 - Second point
 * @returns Distance in degrees
 *
 * @example
 * const dist = calculateDistance(
 *   { lat: 52.0, lng: 20.0 },
 *   { lat: 52.0, lng: 20.001 }
 * );
 * // Returns: ~0.001 degrees (~111m at equator)
 */
export function calculateDistance(p1: Coordinates, p2: Coordinates): number {
  const dLat = p2.lat - p1.lat;
  const dLng = p2.lng - p1.lng;
  return Math.sqrt(dLat * dLat + dLng * dLng);
}
